import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";

export const Route = createFileRoute("/seller-policy")({
  head: () => ({
    meta: [
      { title: "Seller Policy — Want To Buy" },
      { name: "description", content: "Seller obligations on the Want To Buy connection platform: the mandatory 2-photo scam filter, listing accuracy, and the zero-tolerance counterfeit ban." },
      { property: "og:title", content: "Seller Policy — Want To Buy" },
      { property: "og:description", content: "Seller obligations on the Want To Buy connection platform: the mandatory 2-photo scam filter, listing accuracy, and the zero-tolerance counterfeit ban." },
      { name: "twitter:title", content: "Seller Policy — Want To Buy" },
      { name: "twitter:description", content: "Seller obligations on the Want To Buy connection platform: the mandatory 2-photo scam filter, listing accuracy, and the zero-tolerance counterfeit ban." },
    ],
  }),
  component: SellerPolicyPage,
});

export function SellerPolicyContent() {
  return (
    <>
      <p className="!mt-0 text-xs uppercase tracking-wider text-muted-foreground">Last updated: July 2026</p>

      <section className="panel-note" style={{ borderColor: "color-mix(in oklab, var(--color-destructive) 50%, transparent)" }}>
        <h2 className="!mt-0">Zero tolerance for counterfeits and misrepresentation</h2>
        <p>
          <strong>Selling counterfeit, fake, or misrepresented cards results in immediate and permanent account ban with no appeal.</strong> This includes proxy or reprint cards sold as authentic, cards with falsified grading slabs, and any cert number that does not match the card shipped.
        </p>
      </section>

      <section>
        <h2>The one non-negotiable rule: the mandatory 2-photo scam filter</h2>
        <p>
          Before you are connected with the buyer through the message thread, you must submit <strong>exactly 2 timestamped photos</strong>:
        </p>
        <ul>
          <li><strong>Photo 1</strong> — front of the card with a handwritten note in the same frame.</li>
          <li><strong>Photo 2</strong> — back of the card with a handwritten note in the same frame.</li>
        </ul>
        <p>Each handwritten note must contain, written by hand:</p>
        <ul>
          <li>The current date in <strong>DD/MM/YYYY</strong> format.</li>
          <li><code>Seller:</code> followed by your exact Want To Buy username.</li>
          <li><code>Buyer:</code> followed by the buyer's exact Want To Buy username.</li>
          <li>The full order ID.</li>
          <li>The card condition.</li>
        </ul>
        <p>
          For graded cards, one of the two photos must additionally show the slab label with the cert number legible alongside the handwritten note.
        </p>
        <p>
          <strong>This is the one non-negotiable rule on Want To Buy.</strong> It applies to every seller regardless of transaction value or card type (with the exception of Verified Business and Verified Store accounts). Sellers who cannot or will not submit these photos cannot proceed. The 48-hour photo window starts <strong>after the buyer approves the Purchase Confirmation</strong> on offer-based orders, or <strong>immediately after you accept a fixed-price ("Fulfill") order</strong> — not simply at offer acceptance. Failure to submit within that 48-hour window returns the order to open and issues a strike.
        </p>
      </section>

      <section>
        <h2>Identity verification</h2>
        <p>
          To prevent banned sellers from creating new accounts, Want To Buy reserves the right to verify seller identity at any time. Any new account found to be operated by a previously banned user will be permanently banned immediately.
        </p>
      </section>

      <section>
        <h2>Listing accuracy</h2>
        <ul>
          <li>For graded cards, provide the cert number, grading company, and a direct cert lookup link. The cert details must match the actual slab being shipped — buyers are expected to verify the cert link before proceeding.</li>
          <li>For raw cards, disclose every visible flaw (whitening, edge wear, scratches, surface marks) in your offer notes.</li>
          <li>Offer photos must be your own — stock images or competitor photos are grounds for offer rejection.</li>
        </ul>
      </section>

      <section>
        <h2>Where Want To Buy's responsibility ends</h2>
        <p>
          Want To Buy's role ends once you and the buyer are connected through the message thread. From that point on, everything — additional photo requests, payment method, shipping arrangement, and any dispute that arises — is between you and the buyer. Want To Buy strongly recommends conducting the transaction through <strong>PayPal Goods &amp; Services</strong> because PayPal provides independent seller protection that Want To Buy does not and cannot provide.
        </p>
      </section>

      <section>
        <h2>Additional photo requests from buyers</h2>
        <p>
          After the mandatory 2 photos, buyers may request additional photos. You have <strong>24 hours</strong> to respond. Additional photos do not require a new handwritten timestamp.
        </p>
      </section>

      <section>
        <h2>Strikes and bans</h2>
        <p>
          Platform-behaviour violations accumulate strikes as described in the <a href="/rating-policy">Rating &amp; Strike Policy</a>. Severe violations — confirmed counterfeits, identity fraud, ban evasion — result in permanent bans without warning.
        </p>
      </section>

      <section>
        <h2>Agreement at sign-up</h2>
        <p>
          You must explicitly agree to this policy — including the mandatory 2-photo requirement — when creating your account.
        </p>
      </section>
    </>
  );
}

function SellerPolicyPage() {
  return (
    <DocPage eyebrow="Legal" title="Seller Policy" intro="By submitting offers on Want To Buy you agree to the following rules. The mandatory 2-photo requirement is the one non-negotiable rule — everything else is either a platform-integrity policy or a strong recommendation.">
      <SellerPolicyContent />
    </DocPage>
  );
}
